/**
 * Cache Invalidation Strategy
 * Provides utilities to invalidate stale cache entries when data is modified
 */

/* eslint-disable no-console */
// Console statements are intentional for cache invalidation logging

import { clearCachedApiResponse, clearCachedApiResponseByPrefix, createApiCacheKey } from './cache';

/**
 * Cache invalidation patterns for different entity types
 */
export const CacheInvalidationPatterns = {
  // Organization cache patterns
  organizations: [
    '/organizations',
    '/organizations/user',
    '/auth/me',
  ],

  // Contact cache patterns
  contacts: [
    '/contacts',
  ],

  // Transaction cache patterns
  transactions: [
    '/transactions',
    '/transactions/summary',
    '/dashboard',
    '/financial',
  ],

  /** AR/AP mutations that affect transaction list or payment status */
  arAp: [
    '/receivables',
    '/payables',
    '/transactions',
    '/dashboard',
    '/financial',
  ],

  // GST cache patterns
  gst: [
    '/gst/filings',
    '/gst/calendar',
    '/dashboard',
  ],

  // Task cache patterns
  tasks: [
    '/tasks',
    '/tasks/stats',
    '/dashboard',
  ],

  // Document cache patterns
  documents: [
    '/document-management',
    '/document-management/meta',
  ],

  // Approval cache patterns
  approvals: [
    '/approvals',
    '/approvals/pending',
    '/approvals/recent',
    '/approvals/stats',
    '/approvals/activities',
    '/dashboard',
  ],

  // User cache patterns
  users: [
    '/users',
    '/auth/me',
    '/tasks/users',
  ],

  // Notification cache patterns
  notifications: [
    '/notifications',
  ],

  // Dashboard cache patterns
  dashboard: [
    '/dashboard',
  ],
} as const;

/**
 * Invalidate cache for specific entity type
 */
export const invalidateEntityCache = (entityType: keyof typeof CacheInvalidationPatterns): void => {
  const patterns = CacheInvalidationPatterns[entityType];
  
  patterns.forEach((pattern) => {
    clearCachedApiResponse(createApiCacheKey(pattern));
    // Clear parameterized cache keys (e.g. /dashboard/overview, /dashboard/gst with year/orgId params)
    clearCachedApiResponseByPrefix(pattern);
  });
};

/**
 * Invalidate multiple entity caches at once
 */
export const invalidateMultipleEntityCaches = (
  entityTypes: Array<keyof typeof CacheInvalidationPatterns>
): void => {
  entityTypes.forEach((entityType) => {
    invalidateEntityCache(entityType);
  });
};

/**
 * Invalidate all cache entries
 */
export const invalidateAllCache = (): void => {
  const cacheKeys = Object.keys(localStorage);
  cacheKeys.forEach((key) => {
    if (key.startsWith('api_cache_')) {
      localStorage.removeItem(key);
    }
  });
  console.info('All API cache invalidated');
};

/**
 * Invalidate cache based on API action
 * This should be called after successful mutations (POST, PUT, DELETE)
 */
export const invalidateCacheAfterMutation = (
  method: 'POST' | 'PUT' | 'DELETE',
  endpoint: string
): void => {
  // Determine which caches to invalidate based on the endpoint
  if (endpoint.includes('/organizations')) {
    invalidateEntityCache('organizations');
    invalidateEntityCache('dashboard');
  } else if (endpoint.includes('/contacts')) {
    invalidateEntityCache('contacts');
  } else if (endpoint.includes('/transactions')) {
    invalidateEntityCache('transactions');
    invalidateEntityCache('dashboard');
  } else if (
    endpoint.includes('/receivables') ||
    endpoint.includes('/payables')
  ) {
    invalidateEntityCache('arAp');
    invalidateEntityCache('dashboard');
  } else if (endpoint.includes('/gst')) {
    invalidateEntityCache('gst');
    invalidateEntityCache('dashboard');
  } else if (endpoint.includes('/tasks')) {
    invalidateEntityCache('tasks');
    invalidateEntityCache('dashboard');
  } else if (endpoint.includes('/document')) {
    invalidateEntityCache('documents');
  } else if (endpoint.includes('/approvals')) {
    invalidateEntityCache('approvals');
    invalidateEntityCache('dashboard');
  } else if (endpoint.includes('/users')) {
    invalidateEntityCache('users');
  } else if (endpoint.includes('/notifications')) {
    invalidateEntityCache('notifications');
  }

  console.info(`Cache invalidated after ${method} to ${endpoint}`);
};

/**
 * Automatic cache cleanup on app start
 * Removes expired cache entries
 */
export const cleanupExpiredCache = (): void => {
  const now = Date.now();
  const cacheKeys = Object.keys(localStorage);
  let cleanedCount = 0;

  cacheKeys.forEach((key) => {
    if (key.startsWith('api_cache_')) {
      try {
        const cached = JSON.parse(localStorage.getItem(key) || '{}');
        if (cached.expiresAt && cached.expiresAt < now) {
          localStorage.removeItem(key);
          cleanedCount++;
        }
      } catch (error) {
        // Invalid cache entry, remove it
        localStorage.removeItem(key);
        cleanedCount++;
      }
    }
  });

  if (cleanedCount > 0) {
    console.info(`Cleaned up ${cleanedCount} expired cache entries`);
  }
};

/**
 * Get cache statistics
 */
export const getCacheStats = (): {
  totalEntries: number;
  validEntries: number;
  expiredEntries: number;
  totalSizeKB: number;
} => {
  const now = Date.now();
  const cacheKeys = Object.keys(localStorage).filter((key) => key.startsWith('api_cache_'));
  let validCount = 0;
  let expiredCount = 0;
  let totalSize = 0;

  cacheKeys.forEach((key) => {
    try {
      const value = localStorage.getItem(key) || '';
      totalSize += value.length;
      
      const cached = JSON.parse(value);
      if (cached.expiresAt) {
        if (cached.expiresAt >= now) {
          validCount++;
        } else {
          expiredCount++;
        }
      }
    } catch (error) {
      // Invalid entry
      expiredCount++;
    }
  });

  return {
    totalEntries: cacheKeys.length,
    validEntries: validCount,
    expiredEntries: expiredCount,
    totalSizeKB: Math.round((totalSize * 2) / 1024), // Approximate KB (UTF-16)
  };
};

// Run cleanup on module load
if (typeof window !== 'undefined') {
  cleanupExpiredCache();
}


